import { all, call, cancel, fork, put, select, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import { normalize } from 'normalizr';

import { getPatientTemplatesRoutine, getTemplateRoutine, getTemplatesRoutine, templateCacheRoutine } from './routines';
import { getToken, patientTemplates as patientTemplatesApi } from '../../utils/api';
import { doc, templateImage } from '../schemas';
import { chunk } from 'lodash';
import { makeFullCacheSelector } from '../Documents/selectors';
import { loadImg } from '../Documents/sagas';
import { documentCacheRoutine } from '../Documents/routines';


const fullCacheSelector = makeFullCacheSelector()

function* getCacheTemplateSaga(action) {
  const { url: newUrl, date: lastUpdateDate, id } = action.payload;

  for (let i = 0; i < 5; i++) {
    const fullCache = yield select(fullCacheSelector, id);
    const { url: oldCachedUrl, date: cacheDate } =
      typeof fullCache === 'string' || !fullCache ? { url: fullCache, date: 0 } : fullCache;

    try {

      let cachedUrl;
      if (lastUpdateDate < cacheDate) {
        cachedUrl = yield call(loadImg, oldCachedUrl || newUrl);
      } else {
        cachedUrl = yield call(loadImg, newUrl);
      }

      if (oldCachedUrl === cachedUrl) return;

      yield put(documentCacheRoutine.set({ id, url: cachedUrl }));
      return;

    } catch (e) {
      console.warn(`Erreur de cache pour ${id}:`, e);

      // Nettoie le cache local
      yield put(documentCacheRoutine.delete({ id }));

      // ✅ Ne relance le téléchargement du document qu’une seule fois
      if (i === 0 && !/CORS|403|404|401/i.test(e.message)) {
        yield put(getTemplateRoutine.trigger({ id: id.split('_')[0] }));
      }

      // Backoff exponentiel
      if (i < 4) {
        yield delay((2 ** (i + 1)) * 100);
      } else {
        console.error(`Abandon après ${i + 1} tentatives pour ${id}`);
      }
    }
  }
}

export function* getPatientTemplatesSaga({ payload }) {
  yield put(getPatientTemplatesRoutine.request());

  try {
    const token = yield select(getToken);
    const { data } = yield call(patientTemplatesApi.getPatientTemplates, token, payload.patientId);

    const normalizedData = normalize(data, [doc]);

    yield put(getPatientTemplatesRoutine.success(normalizedData));
  } catch (error) {
    yield put(getPatientTemplatesRoutine.failure(error));
  }

  yield put(getPatientTemplatesRoutine.fulfill());
}

export function* getTemplatesSaga({ payload }) {
  const { folderId, templateNames } = payload;
  yield put(getTemplatesRoutine.request(null, { folderId }));

  try {
    const token = yield select(getToken);
    const { data } = yield call(
      patientTemplatesApi.getTemplates,
      token,
      folderId,
      templateNames
    );

    const normalizedData = normalize(data, [templateImage]);
    yield put(getTemplatesRoutine.success(normalizedData, { folderId }));
  } catch (error) {
    yield put(getTemplatesRoutine.failure(error, { folderId }));
  }

  yield put(getTemplatesRoutine.fulfill(null, { folderId }));
}

let pendingTemplatesByFolder = {}; // { folderId: { name1: true, name2: true } }
let task;

function* fetchTemplates() {
  yield delay(50); // debounce

  const folders = Object.entries(pendingTemplatesByFolder);

  yield all(
    folders.map(([folderId, namesObj]) => {
      const names = Object.keys(namesObj);
      return all(
        chunk(names, 20).map((nameChunk) =>
          fork(getTemplatesSaga, {
            payload: { folderId, templateNames: nameChunk },
          })
        )
      );
    })
  );

  pendingTemplatesByFolder = {};
  task = null;
}

export function* accumulateGetTemplate({ payload }) {
  const [ folderId, templateName ] = payload?.id.split(':');

  if (!pendingTemplatesByFolder[folderId]) {
    pendingTemplatesByFolder[folderId] = {};
  }

  pendingTemplatesByFolder[folderId][templateName] = true;

  if (task) {
    yield cancel(task);
  }
  task = yield fork(fetchTemplates);
}

export default function* patientTemplatesSaga() {
  yield all([
    takeLatest(getPatientTemplatesRoutine.TRIGGER, getPatientTemplatesSaga),
    takeLatest(getTemplatesRoutine.TRIGGER, getTemplatesSaga),
    takeEvery(getTemplateRoutine.TRIGGER, accumulateGetTemplate),
    takeEvery(templateCacheRoutine.GET, getCacheTemplateSaga),
  ]);
}
